import {
    Billboard,
    BillboardCollection,
    Cartesian3,
    Cartographic,
    Color,
    DeveloperError,
    Event,
    Math as CesiumMath,
    PointPrimitive,
    PointPrimitiveCollection
} from "cesium";

import * as turf from "@turf/turf";
import { PointOptions, PolylinePrimitive, PolygonPrimitive } from "../../core";
import { editingPointOptions } from "./EditCommon";
import { nearestPointIndex, nearestEdgeIndex } from "./util";

const PIXEL_TOLERANCE = 20;

const cart3Scratch1 = new Cartesian3();

export class EditablePolygon {
    private readonly _billboardCollection: BillboardCollection;
    private readonly _pointPrimitiveCollection: PointPrimitiveCollection;
    private readonly _pointOptions: PointOptions;
    private readonly _points: PointPrimitive[];
    private readonly _polyline: PolylinePrimitive;
    private readonly _polygon: PolygonPrimitive;
    private readonly _middleVertices: Billboard[] = [];
    private readonly _originalPositions: Cartesian3[];
    private readonly _originalColor: Color;
    private readonly _middleVertexOptions = {
        image: `${window.Construkted.imageRootUrl()}/add.png`,
        disableDepthTestDistance: Infinity
    };

    private readonly _vertexChanged: Event;
    private _turfPolygon: turf.Feature<turf.Polygon>;
    private _editing: boolean;
    private readonly _showVertices: boolean;

    constructor(options: {
        billboardCollection: BillboardCollection;
        pointPrimitiveCollection: PointPrimitiveCollection;
        pointOptions: PointOptions;
        points: PointPrimitive[];
        polyline: PolylinePrimitive;
        polygon: PolygonPrimitive;
        showVertices: boolean;
    }) {
        this._billboardCollection = options.billboardCollection;
        this._pointPrimitiveCollection = options.pointPrimitiveCollection;
        this._pointOptions = options.pointOptions;

        this._points = options.points;
        this._polyline = options.polyline;
        this._polygon = options.polygon;

        this._originalPositions = [];
        this._polygon.positions.forEach((position) => {
            this._originalPositions.push(position.clone(new Cartesian3()));
        });

        this._originalColor = new Color();
        this._polygon.color.clone(this._originalColor);
        this._vertexChanged = new Event();

        this._turfPolygon = this._createTurfPolygon();
        this._editing = false;
        this._showVertices = options.showVertices;
    }

    get pointPrimitiveCollection() {
        return this._pointPrimitiveCollection;
    }

    get pointPrimitives() {
        return this._points;
    }

    get polylinePrimitive() {
        return this._polyline;
    }

    get polygonPrimitive() {
        return this._polygon;
    }

    get middleVertices() {
        return this._middleVertices!;
    }

    get vertexChanged() {
        return this._vertexChanged;
    }

    get editing() {
        return this._editing;
    }

    showHide(show: boolean) {
        if (this._showVertices) {
            this._points.forEach((point) => {
                point.show = show;
            });
        }

        this._polyline.show = show;
        this._polygon.show = show;
    }

    getMainVertex(index: number) {
        return this._points[index];
    }

    updateTurfPolygon() {
        this._turfPolygon = this._createTurfPolygon();
    }

    _createTurfPolygon() {
        const lonlatsInDegree: Array<[number, number]> = [];

        this._polygon.positions.forEach((pos) => {
            const carto = Cartographic.fromCartesian(pos);

            lonlatsInDegree.push([CesiumMath.toDegrees(carto.longitude), CesiumMath.toDegrees(carto.latitude)]);
        });

        lonlatsInDegree.push(lonlatsInDegree[0]);

        return turf.polygon([lonlatsInDegree], { name: "" });
    }

    isContainPosition(position: Cartesian3) {
        const carto = Cartographic.fromCartesian(position);
        const turfPoint = turf.point([CesiumMath.toDegrees(carto.longitude), CesiumMath.toDegrees(carto.latitude)]);

        return turf.booleanContains(this._turfPolygon, turfPoint);
    }

    nearestEdgeIndex(position: Cartesian3) {
        return nearestEdgeIndex({
            scene: window.Construkted.scene,
            position: position,
            points: this._points,
            isPolygon: true,
            pixelTolerance: PIXEL_TOLERANCE
        });
    }

    nearestMainVertexIndex(position: Cartesian3) {
        return nearestPointIndex({
            scene: window.Construkted.scene,
            position: position,
            points: this._points,
            pixelTolerance: PIXEL_TOLERANCE
        });
    }

    startEditing() {
        this._createMiddleVertices();
        this._editing = true;
    }

    setGeneralEditingStyleForAllMainVertices() {
        const pointOptions = editingPointOptions;

        this._points.forEach((point) => {
            point.color = pointOptions.color;
            point.pixelSize = pointOptions.pixelSize;
            point.outlineWidth = pointOptions.outlineWidth;
            point.outlineColor = pointOptions.outlineColor;
            point.show = this._showVertices;
        });
    }

    setVertexEditingStyleForMainVertex(index: number) {
        const point = this._points[index];

        point.pixelSize = editingPointOptions.pixelSize + 5;
        point.show = true;
    }

    setOriginalStyleForAllMainVertices() {
        const pointOptions = this._pointOptions;

        this._points.forEach((point) => {
            point.color = pointOptions.color!;
            point.pixelSize = pointOptions.pixelSize!;
            point.outlineWidth = 0;
            point.show = this._showVertices;
        });
    }

    showMiddleVertex(vertexIndex: number) {
        this._middleVertices[vertexIndex].show = true;
    }

    hideMiddleVertices() {
        this._middleVertices.forEach((billboard) => {
            billboard.show = false;
        });
    }

    private _createMiddleVertices() {
        const positions: Cartesian3[] = [];

        this._points.forEach((point) => {
            positions.push(point.position);
        });

        for (let i = 0; i < positions.length; i++) {
            const firstPosition = positions[i];

            let lastPosition;

            if (i === positions.length - 1) {
                lastPosition = positions[0];
            } else {
                lastPosition = positions[i + 1];
            }

            const midPosition = Cartesian3.midpoint(firstPosition, lastPosition, new Cartesian3());

            const middleVertex = this._billboardCollection.add({
                ...this._middleVertexOptions,
                position: midPosition,
                show: false
            });

            this._middleVertices.push(middleVertex);
        }
    }

    restoreMainVertices() {
        const positions: Cartesian3[] = [];

        this._points.forEach((point) => {
            positions.push(point.position);
        });

        this._points.forEach((point) => {
            this._pointPrimitiveCollection.remove(point);
        });

        this._points.length = 0;

        positions.forEach((position) => {
            const point = this._pointPrimitiveCollection.add({
                ...this._pointOptions,
                show: this._showVertices,
                position: position
            });

            this._points.push(point);
        });
    }

    removeMiddleVertices() {
        for (let i = 0; i < this._middleVertices.length; i++) {
            this._billboardCollection.remove(this._middleVertices[i]);
        }

        this._middleVertices.length = 0;
    }

    isContainedVertex(point: PointPrimitive) {
        for (let i = 0; i < this._points.length; i++) {
            if (Object.is(point, this._points[i])) {
                return true;
            }
        }

        for (let i = 0; i < this._middleVertices.length; i++) {
            if (Object.is(point, this._middleVertices[i])) {
                return true;
            }
        }

        return false;
    }

    indexOfMainVertices(primitive: any) {
        for (let i = 0; i < this._points.length; i++) {
            if (Object.is(primitive, this._points[i])) {
                return i;
            }
        }

        return -1;
    }

    indexOfMiddleVertices(primitive: any) {
        for (let i = 0; i < this._middleVertices.length; i++) {
            if (Object.is(primitive, this._middleVertices[i])) {
                return i;
            }
        }

        return -1;
    }

    updateMainVertex(index: number, position: Cartesian3) {
        if (index < 0 || index > this._polygon.positions.length - 1) {
            throw new DeveloperError("error");
        }

        this._points[index].position = position;

        this._polyline.updatePosition(index, position);
        this._polygon.updatePosition(index, position);

        const positions = this._points.map((point) => point.position);
        const positionCount = positions.length;

        let prevMiddleVertexPointPrimitive;
        let nextPosition;
        let prevPosition;

        if (index === 0) {
            prevPosition = positions[positionCount - 1];
            prevMiddleVertexPointPrimitive = this._middleVertices[positionCount - 1];
        } else {
            prevPosition = positions[index - 1];
            prevMiddleVertexPointPrimitive = this._middleVertices[index - 1];
        }

        if (index === positionCount - 1) {
            nextPosition = positions[0];
        } else {
            nextPosition = positions[index + 1];
        }

        this._middleVertices[index].position = Cartesian3.midpoint(position, nextPosition, cart3Scratch1);
        prevMiddleVertexPointPrimitive.position = Cartesian3.midpoint(position, prevPosition, cart3Scratch1);

        this._vertexChanged.raiseEvent();
    }

    insertMainVertex(indexOfMiddleVertex: number) {
        const positions = this._polygon.positions;
        const position = positions[indexOfMiddleVertex];

        let nextPosition;

        if (indexOfMiddleVertex === positions.length - 1) {
            nextPosition = positions[0].clone(new Cartesian3());
        } else {
            nextPosition = positions[indexOfMiddleVertex + 1].clone(new Cartesian3());
        }

        const middleVertex = this._middleVertices[indexOfMiddleVertex];

        const newMainVertex = this._pointPrimitiveCollection.add({
            ...this._pointOptions,
            ...editingPointOptions,
            show: this._showVertices,
            position: middleVertex.position
        });

        this._points.splice(indexOfMiddleVertex + 1, 0, newMainVertex);

        this._billboardCollection.remove(middleVertex);

        const middleVertexPosition = middleVertex.position;

        this._polygon.insertPosition(indexOfMiddleVertex + 1, middleVertexPosition);

        // note that polygon and polyline share positions property
        this._polyline.forceUpdate();
        this._polygon.forceUpdate();

        const newMiddleVertex1 = this._billboardCollection.add({
            ...this._middleVertexOptions,
            position: Cartesian3.midpoint(position, middleVertexPosition, new Cartesian3())
        });

        const newMiddleVertex2 = this._billboardCollection.add({
            ...this._middleVertexOptions,
            position: Cartesian3.midpoint(middleVertexPosition, nextPosition, new Cartesian3())
        });

        this._middleVertices.splice(indexOfMiddleVertex, 1, newMiddleVertex1, newMiddleVertex2);

        return newMainVertex;
    }

    cancelEditing() {
        this._editing = false;

        this._revertToOriginal();
    }

    _revertToOriginal() {
        this.removeMiddleVertices();

        this._points.forEach((point) => {
            this._pointPrimitiveCollection.remove(point);
        });

        this._points.length = 0;

        this._originalPositions.forEach((position) => {
            const point = this._pointPrimitiveCollection.add({
                ...this._pointOptions,
                show: this._showVertices,
                position: position
            });

            this._points.push(point);
        });

        const positions: Cartesian3[] = [];

        this._originalPositions.forEach((position) => {
            positions.push(position.clone(new Cartesian3()));
        });

        this._polyline.positions = positions;
        this._polygon.positions = positions;

        this._polygon.color = this._originalColor;

        this._vertexChanged.raiseEvent();
    }

    removeMainVertex(indexOfMainVertex: number) {
        this._polygon.removePositon(indexOfMainVertex);

        // note that polygon and polyline share positions property
        this._polyline.forceUpdate();
        this._polygon.forceUpdate();

        const mainVertex = this._points[indexOfMainVertex];

        this._points.splice(indexOfMainVertex, 1);

        this._pointPrimitiveCollection.remove(mainVertex);

        this._middleVertices.forEach((point) => {
            this._billboardCollection.remove(point);
        });

        this._middleVertices.length = 0;

        this._createMiddleVertices();

        this._vertexChanged.raiseEvent();

        this.updateTurfPolygon();
    }

    isContained(primitive: any): boolean {
        const index = this.indexOfMainVertices(primitive);

        if (index !== -1) {
            return true;
        }

        if (Object.is(primitive, this._polyline.primitive)) {
            return true;
        }

        if (Object.is(primitive, this._polygon.primitive)) {
            return true;
        }

        return false;
    }

    showHideMiddleVertice(show: boolean) {
        this._middleVertices.forEach((billboard) => {
            billboard.show = show;
        });
    }

    updatePositions(positions: Cartesian3[]) {
        this._polygon.positions = positions;
        this._polyline.positions = positions;

        for (let i = 0; i < this._pointPrimitiveCollection.length; i++) {
            this._pointPrimitiveCollection.get(i).position = positions[i];
        }

        this._originalPositions.length = 0;
        positions.forEach((position) => {
            this._originalPositions.push(position.clone(new Cartesian3()));
        });
    }
}
